import React, { useEffect, useState } from "react";
import { XCircle, TrendingDown, Lightbulb } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, Section, StatCard, LoadingBlock, EmptyState } from "../../components/app/ui";
import ReportFilters, { defaultFilter, toParams } from "../../components/app/ReportFilters";
import { restaurantApi, inr, errorMessage } from "../../lib/api";

const TIPS = {
  "out of stock": "Frequent out-of-stock rejections? Mark items unavailable in your Menu so customers don't order them.",
  "item unavailable": "Keep your menu availability updated so unavailable items aren't shown to customers.",
  "kitchen too busy": "Busy-hour rejections? Narrow your opening hours or pause online orders during rushes.",
  "closing soon": "Rejections near closing time? Tighten your closing hours so late orders aren't placed.",
  "can't deliver to area": "Out-of-area rejections? Set a realistic delivery radius in Restaurant info.",
};

function tipFor(reason) {
  const k = (reason || "").toLowerCase();
  for (const key of Object.keys(TIPS)) if (k.includes(key)) return TIPS[key];
  return null;
}

export default function RejectAnalytics() {
  const [filter, setFilter] = useState(defaultFilter);
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    restaurantApi.rejectAnalytics(toParams(filter))
      .then(setData)
      .catch((e) => { toast.error(errorMessage(e)); setData({ by_reason: [], total_rejected: 0 }); });
  }, [filter]);

  const rows = data?.by_reason || [];
  const topReason = rows[0];

  return (
    <>
      <PageHeader title="Reject reasons" description="Understand why orders get rejected so you can fix stock and timing." />
      <ReportFilters value={filter} onChange={setFilter} />
      {data === null ? <LoadingBlock rows={3} /> : (
        <>
          <div className="mb-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            <StatCard label="Orders rejected" value={data.total_rejected || 0} hint={data.window} icon={XCircle} tone="brand" />
            <StatCard label="Lost order value" value={inr(rows.reduce((s, r) => s + (r.value || 0), 0))} icon={TrendingDown} tone="ink" />
            <StatCard label="Top reason" value={topReason ? topReason.reason : "—"} hint={topReason ? `${topReason.count} orders` : "No rejections"} icon={XCircle} tone="graphite" />
          </div>

          {topReason && tipFor(topReason.reason) && (
            <div className="mb-6 flex items-start gap-3 rounded-2xl border border-brand-200 bg-brand-50 p-4" data-testid="reject-tip">
              <Lightbulb className="mt-0.5 h-5 w-5 shrink-0 text-brand" />
              <p className="text-sm text-ink"><strong>Tip:</strong> {tipFor(topReason.reason)}</p>
            </div>
          )}

          <Section title="Breakdown by reason" description={data.window}>
            {rows.length === 0 ? (
              <EmptyState icon={XCircle} title="No rejections 🎉" text="You haven't rejected any orders in this period." />
            ) : (
              <ul className="space-y-3" data-testid="reject-breakdown">
                {rows.map((r) => (
                  <li key={r.reason} data-testid={`reject-row-${r.reason.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                    <div className="mb-1 flex items-center justify-between text-sm">
                      <span className="font-medium text-ink">{r.reason}</span>
                      <span className="text-ink-soft">{r.count} orders · {inr(r.value)} · {r.pct}%</span>
                    </div>
                    <div className="h-2.5 overflow-hidden rounded-full bg-surface-200">
                      <div className="h-full rounded-full bg-brand" style={{ width: `${Math.max(4, r.pct)}%` }} />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Section>
        </>
      )}
    </>
  );
}
